import { APP_ENVIRONMENT, APP_VERSION, ConfigSettingsService, TelemetryService } from '@ebeihilfe/core';
import { ErrorEvent, EventHint, browserTracingIntegration, init } from '@sentry/angular';
import { Injectable, inject } from '@angular/core';
import { ModalService } from '@ebeihilfe/web-logic';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private readonly configService = inject(ConfigSettingsService);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly modalService = inject(ModalService);
  private readonly telemetryService = inject(TelemetryService);
  private readonly translate = inject(TranslateService);
  private readonly versionToken = inject(APP_VERSION);

  private domainRegex = /^https?:\/\/([a-z0-9]+(?:\.[a-z0-9]+)*\.[a-z]+\.[a-z]+)\/res$/;
  private isInitialized = false;

  public async init(): Promise<void> {
    const userHasEnabled = await this.telemetryService.userHasTelemetryEnabled();

    if (this.environment.sentry && this.environment.sentry.dsn && !this.isInitialized && userHasEnabled) {
      init({
        beforeSend: this.beforeSend.bind(this),
        dsn: this.environment.sentry.dsn,
        environment: this.getEnvironment(),
        integrations: [browserTracingIntegration()],
        release: this.versionToken.version + '-' + this.versionToken.buildNumber,
        // Session Replay
        replaysSessionSampleRate: this.environment.sentry.replaysSessionSampleRate ?? 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: this.environment.sentry.replaysOnErrorSampleRate ?? 0.3, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        // Tracing
        tracesSampleRate: this.environment.sentry.tracesSampleRate ?? 0.1, //  Capture 10% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: this.environment.sentry.tracePropagationTargets ?? ['localhost'],
      });
      this.isInitialized = true;
    }
  }

  private async beforeSend(event: ErrorEvent, hint: EventHint): Promise<ErrorEvent | null> {
    const userHasEnabled = await this.telemetryService.userHasTelemetryEnabled();

    if (!userHasEnabled || !event.exception || !event.event_id || !hint.originalException) {
      return null;
    }
    const result = await this.modalService.openConfirmationModal({
      content: this.translate.instant('general.errorhandler.modal.header'),
    });
    return result ? event : null;
  }

  private getEnvironment(): string {
    const customer = this.configService.appSettings.connection.res.match(this.domainRegex);
    const customerId = customer ? customer[1] : 'unknown';
    const env = this.environment.production ? 'production' : 'development';
    return customerId + '-' + env;
  }
}
